import { humanDay, humanPeriod, parseFreshnessLine } from '@/lib/freshnessLine';
import {
  ChevronDown,
  CircleCheck,
  Database,
  TriangleAlert
} from 'lucide-react';
import { useState } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';

/**
 * The three framed blocks a Signals answer ends with.
 *
 * All copy here is English on purpose: a notice the system writes is English
 * whatever language the question was in. Only the model's own sentences follow
 * the question's language, and those arrive as content.
 */

/**
 * The caveat. Replaces the italic blockquote, which read as a pull-quote —
 * decorative — when it is usually the sentence that stops a number being
 * misread.
 */
export const Callout = ({ children }: React.PropsWithChildren) => (
  <div
    className="flex gap-[10px]"
    style={{
      border: '1px solid var(--sig-warn-border)',
      background: 'var(--sig-warn-bg)',
      borderRadius: 11,
      padding: '11px 13px'
    }}
  >
    <TriangleAlert
      size={16}
      strokeWidth={2}
      style={{ color: 'var(--sig-warn-icon)', flex: 'none', marginTop: 2 }}
    />
    <div
      className="text-[13px] sig-pretty"
      style={{ lineHeight: 1.65, color: 'var(--sig-warn-text)' }}
      dir="auto"
    >
      {children}
    </div>
  </div>
);

/**
 * "Where this came from", collapsed by default.
 *
 * The model already closes every answer with a one-line method statement. It
 * used to sit inline as an English paragraph in the middle of an Arabic answer;
 * here it keeps its place in the record without competing with the answer.
 */
export const Provenance = ({
  summary,
  children
}: React.PropsWithChildren<{ summary: string }>) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        border: '1px solid var(--sig-border-light)',
        borderRadius: 11,
        background: 'var(--sig-panel)',
        overflow: 'hidden'
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center justify-between gap-[10px] w-full text-left"
        style={{ padding: '10px 13px' }}
      >
        <span
          className="flex items-center gap-2 text-[12.5px] font-semibold min-w-0"
          style={{ color: 'var(--sig-body)', fontFamily: 'Inter, sans-serif' }}
        >
          <Database size={14} strokeWidth={2} style={{ flex: 'none' }} />
          <span className="truncate">{summary}</span>
        </span>
        <ChevronDown
          size={14}
          strokeWidth={2.4}
          style={{
            color: 'var(--sig-subtle)',
            flex: 'none',
            transform: open ? 'rotate(180deg)' : undefined,
            transition: 'transform .15s'
          }}
        />
      </button>
      {open ? (
        <div
          style={{
            borderTop: '1px solid var(--sig-border-light)',
            padding: '11px 13px',
            fontSize: 12,
            lineHeight: 1.6,
            color: 'var(--sig-muted)'
          }}
          dir="auto"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
};

/**
 * The next question the backend offered.
 *
 * It already exists in step metadata as `next_question`, where it was being
 * spent as a placeholder inside the empty composer — visible only to a user who
 * had not started typing, and gone the moment they did. A chip under the answer
 * is the better surface for it.
 */
export const FollowUpChips = ({
  questions,
  onPick
}: {
  questions: string[];
  onPick: (q: string) => void;
}) => {
  if (!questions.length) return null;
  return (
    <div className="flex flex-wrap gap-[7px] pt-0.5">
      {questions.map((q) => (
        <button
          key={q}
          type="button"
          onClick={() => onPick(q)}
          dir="auto"
          className="sig-followup-chip text-[12.5px] transition-colors"
          style={{
            color: 'var(--sig-blue-deep)',
            border: '1px solid var(--sig-border-blue)',
            background: 'var(--sig-chip)',
            borderRadius: 999,
            padding: '6px 12px'
          }}
        >
          {q}
        </button>
      ))}
    </div>
  );
};

/**
 * The freshness stamp the model writes as its last line ("Data updated through
 * ..."). Kept, but demoted out of the answer's body text: it is a property of
 * the data, not a sentence in the reply. September 2026 is why it is never
 * dropped.
 *
 * Shown as pills, like the header's "Data through" pill, each fact with a
 * sentence of its own in a tooltip. As one line -- "loaded 24 Sep 01:00 ...
 * checked 24 Sep" -- the owner could not tell what had happened when
 * (2026-09-24). A line the parser cannot read is shown whole, never dropped.
 */
const pillStyle = (tone: 'neutral' | 'good' | 'warn'): React.CSSProperties => ({
  color: tone === 'warn' ? 'var(--sig-warn-text)' : 'var(--sig-muted)',
  background: tone === 'warn' ? 'var(--sig-warn-bg)' : 'var(--sig-panel-deep)',
  border: `1px solid ${tone === 'warn' ? 'var(--sig-warn-border)' : 'var(--sig-border)'}`,
  borderRadius: 999,
  padding: '4px 9px',
  fontFamily: 'Inter, sans-serif',
  cursor: 'default'
});

const Dot = ({ color }: { color: string }) => (
  <span
    style={{
      width: 6,
      height: 6,
      borderRadius: 999,
      background: color,
      display: 'block',
      flex: 'none'
    }}
  />
);

const Pill = ({
  tone,
  tip,
  children
}: React.PropsWithChildren<{
  tone: 'neutral' | 'good' | 'warn';
  tip: string;
}>) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        className="inline-flex items-center gap-1.5 text-[11.5px]"
        dir="ltr"
        style={pillStyle(tone)}
        title={tip}
        tabIndex={0}
      >
        {children}
      </span>
    </TooltipTrigger>
    <TooltipContent className="max-w-[280px] text-[12px] leading-snug">
      {tip}
    </TooltipContent>
  </Tooltip>
);

export const FreshnessStamp = ({ text }: { text: string }) => {
  const facts = parseFreshnessLine(text);
  if (!facts.through) {
    return (
      <Pill tone="neutral" tip="How recent the data behind this answer is.">
        <Dot color="var(--sig-positive)" />
        {facts.raw}
      </Pill>
    );
  }

  const readTip = facts.loadedAt
    ? `Read from your ERP on ${humanDay(facts.loadedAt)}. Anything entered in ` +
      `the ERP after that appears after the next nightly load.`
    : 'The most recent day in the data behind this answer.';

  const erp = facts.erp;
  const when = erp?.checkedAt ? ` on ${humanDay(erp.checkedAt)}` : '';
  const periods = (erp?.periods || []).map(humanPeriod).join(', ');

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Pill tone="neutral" tip={readTip}>
        <Dot color="var(--sig-positive)" />
        Data through {humanDay(facts.through)}
      </Pill>
      {erp?.state === 'match' ? (
        <Pill
          tone="good"
          tip={`Checked against your ERP's own database${when}: every entry and amount in this period matched.`}
        >
          <CircleCheck
            size={12}
            strokeWidth={2.4}
            style={{ color: 'var(--sig-positive)' }}
          />
          Matches your ERP
        </Pill>
      ) : null}
      {erp?.state === 'loading' ? (
        <Pill
          tone="good"
          tip={
            `Checked against your ERP's own database${when}. It matched, except ` +
            `entries made in the ERP since the last load${periods ? ` (${periods})` : ''}, ` +
            'which arrive with the next nightly load.'
          }
        >
          <CircleCheck
            size={12}
            strokeWidth={2.4}
            style={{ color: 'var(--sig-positive)' }}
          />
          Matches your ERP · newest entries loading
        </Pill>
      ) : null}
      {erp?.state === 'mismatch' ? (
        <Pill
          tone="warn"
          tip={
            `Checked against your ERP's own database${when}: ${periods || 'this period'} ` +
            'did not match. It is being repaired overnight; treat these figures with care until then.'
          }
        >
          <TriangleAlert
            size={12}
            strokeWidth={2.4}
            style={{ color: 'var(--sig-warn-icon)' }}
          />
          Differs from your ERP{periods ? ` · ${periods}` : ''}
        </Pill>
      ) : null}
    </div>
  );
};

/**
 * The item an answer is about: its photo, name and code, above the answer.
 *
 * The server sends it as the answer's first line, an ordinary markdown image
 * titled `signals:item` (signals-copilot server/item_card.py), only when the
 * customer's ERP keeps a photo for the item. Drawn small and framed like the
 * answer's other blocks; the default markdown image was a 16:9 grey box as
 * wide as the answer, which made a product photo the loudest thing on screen.
 *
 * A photo that fails to load removes the whole header: the answer already
 * names the item, and an empty frame would only look broken.
 */
export const ITEM_HEADER_TITLE = 'signals:item';

export const ItemHeader = ({ src, label }: { src: string; label?: string }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  const text = label || '';
  const cut = text.lastIndexOf(' · ');
  const name = cut > 0 ? text.slice(0, cut) : text;
  const code = cut > 0 ? text.slice(cut + 3) : '';
  return (
    <div
      className="flex items-center gap-3"
      style={{
        border: '1px solid var(--sig-border-light)',
        borderRadius: 11,
        background: 'var(--sig-panel)',
        padding: 8,
        paddingInlineEnd: 14,
        width: 'fit-content',
        maxWidth: '100%'
      }}
      dir="auto"
    >
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        title={name}
        className="flex items-center justify-center overflow-hidden"
        style={{
          width: 72,
          height: 72,
          flex: 'none',
          borderRadius: 8,
          background: '#fff',
          border: '1px solid var(--sig-border-light)'
        }}
      >
        <img
          src={src}
          alt={name}
          loading="lazy"
          onError={() => setFailed(true)}
          className="max-h-full max-w-full object-contain"
          style={{ padding: 4 }}
        />
      </a>
      <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
        <span
          className="font-semibold text-[14px] sig-pretty"
          style={{ color: 'var(--sig-ink)', lineHeight: 1.4 }}
          dir="auto"
        >
          {name}
        </span>
        {code ? (
          <span
            className="text-[12px] font-mono"
            style={{ color: 'var(--sig-muted)' }}
            dir="ltr"
          >
            {code}
          </span>
        ) : null}
      </div>
    </div>
  );
};
